import React, { useState, useEffect } from 'react';
import api from '../services/api';
import SearchableSelect from './SearchableSelect';
import { useToast } from '../context/ToastContext';

interface Staff {
  id: number;
  name: string;
}

interface Parent {
  id: number;
  name: string;
}

interface Project {
  id?: number;
  title: string;
  description: string;
  origin: string;
  status: string;
  responsible?: number;
  proposer_ids?: number[];
  proposers?: { id: number; name: string }[];
  expected_completion_date?: string;
}

interface ProjectFormProps {
  onSuccess: () => void;
  onCancel: () => void;
  projectToEdit?: Project | null;
}

const ProjectForm: React.FC<ProjectFormProps> = ({ onSuccess, onCancel, projectToEdit }) => {
  const { addToast } = useToast();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [origin, setOrigin] = useState('');
  const [status, setStatus] = useState('Proposed');
  const [responsible, setResponsible] = useState<number | ''>('');
  const [selectedProposers, setSelectedProposers] = useState<number[]>([]);
  const [expectedCompletionDate, setExpectedCompletionDate] = useState('');
  
  const [allStaff, setAllStaff] = useState<Staff[]>([]);
  const [allParents, setAllParents] = useState<Parent[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [staffRes, parentsRes] = await Promise.all([
          api.get(`staff/?active_only=true`),
          api.get(`parents/?active_only=true`)
        ]);
        setAllStaff(staffRes.data);
        setAllParents(parentsRes.data);
      } catch (error) {
        console.error('Failed to fetch data', error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (projectToEdit) {
      setTitle(projectToEdit.title);
      setDescription(projectToEdit.description);
      setOrigin(projectToEdit.origin);
      setStatus(projectToEdit.status);
      setResponsible(projectToEdit.responsible || '');
      setExpectedCompletionDate(projectToEdit.expected_completion_date || '');
      
      let proposerIds: number[] = [];
      if (projectToEdit.proposers) {
        proposerIds = projectToEdit.proposers.map(p => p.id);
      } else if (projectToEdit.proposer_ids) {
        proposerIds = projectToEdit.proposer_ids;
      }
      setSelectedProposers(proposerIds);
    }
  }, [projectToEdit]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const projectData = { 
      title, 
      description, 
      origin, 
      status,
      responsible: responsible || null,
      proposer_ids: selectedProposers,
      expected_completion_date: expectedCompletionDate || null
    };

    try {
      if (projectToEdit && projectToEdit.id) {
        await api.patch(`projects/${projectToEdit.id}/`, projectData);
        addToast('Projeto atualizado com sucesso!', 'success');
      } else {
        await api.post(`projects/`, projectData);
        addToast('Nova proposta de projeto cadastrada!', 'success');
      }
      onSuccess();
    } catch (error) {
      console.error('Failed to save project', error);
      addToast('Erro ao salvar projeto.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const SectionTitle = ({ title, icon }: { title: string, icon: string }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '15px', marginTop: '10px' }}>
      <span style={{ fontSize: '1.2rem' }}>{icon}</span>
      <h4 style={{ margin: 0, fontSize: '0.95rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--primary-blue)' }}>{title}</h4>
    </div>
  );

  return (
    <div className="card" style={{ maxWidth: '850px', margin: '0 auto' }}>
      <div style={{ marginBottom: '25px', borderBottom: '1px solid var(--border-color)', paddingBottom: '15px' }}>
        <h3 style={{ margin: 0 }}>{projectToEdit ? '💡 Editar Projeto' : '🚀 Nova Proposta de Projeto'}</h3>
      </div>

      <form onSubmit={handleSubmit}>
        <SectionTitle title="Definição da Proposta" icon="📝" />
        <div className="form-group">
          <label>Título do Projeto</label>
          <input 
            type="text" 
            value={title} 
            onChange={(e) => setTitle(e.target.value)} 
            placeholder="Ex: Horta Comunitária Educativa"
            required 
          />
        </div>

        <div className="form-group">
          <label>Descrição e Objetivos</label>
          <textarea 
            value={description} 
            onChange={(e) => setDescription(e.target.value)} 
            placeholder="Descreva detalhadamente a proposta e o impacto esperado..."
            style={{ height: '100px' }}
            required
          />
        </div>
        
        <div style={{ marginTop: '30px' }}>
          <SectionTitle title="Planejamento e Status" icon="⚙️" />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          <div className="form-group">
            <label>Origem (Onde surgiu a ideia?)</label>
            <input 
              type="text" 
              value={origin} 
              onChange={(e) => setOrigin(e.target.value)} 
              placeholder="Ex: Reunião de Pais de Março"
              required 
            />
          </div>
          <div className="form-group">
            <label>Status do Projeto</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="Proposed">Proposto</option>
              <option value="InReview">Em Análise</option>
              <option value="Approved">Aprovado</option>
              <option value="Implemented">Implementado</option>
              <option value="Rejected">Rejeitado</option>
            </select>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          <div className="form-group">
            <label>Previsão de Conclusão</label>
            <input 
              type="date" 
              value={expectedCompletionDate} 
              onChange={(e) => setExpectedCompletionDate(e.target.value)} 
            />
          </div>
          
          <SearchableSelect 
            label="Responsável (Escola)"
            options={allStaff}
            value={responsible}
            onChange={setResponsible}
            placeholder="Quem cuidará deste projeto?"
          />
        </div>
        
        <div style={{ marginTop: '10px' }}>
          <SearchableSelect 
            label="Pais Proponentes / Envolvidos"
            options={allParents}
            value={selectedProposers}
            onChange={setSelectedProposers}
            multiple
            placeholder="Selecione os pais envolvidos..."
          />
        </div>

        <div style={{ 
          marginTop: '40px', 
          paddingTop: '20px', 
          borderTop: '1px solid var(--border-color)',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px'
        }}>
          <button type="button" onClick={onCancel} className="btn btn-secondary">Cancelar</button>
          <button type="submit" className="btn" disabled={loading}>
            {loading ? 'Salvando...' : 'Salvar Proposta'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProjectForm;
